/**
 * Form State Management
 * Manages form state including edit mode, validation state, dirty fields, touched fields
 */

export class FormState {
  constructor() {
    this.editMode = false;
    this.entryId = null;
    this.dirtyFields = new Set();
    this.touchedFields = new Set();
    this.fieldInitialValues = new Map();
    this.validationState = new Map();
    this.formData = {};
  }

  /**
   * Set edit mode
   * @param {boolean} editMode - Edit mode flag
   * @param {string} entryId - Entry ID if in edit mode
   */
  setEditMode(editMode, entryId = null) {
    this.editMode = editMode;
    this.entryId = entryId;
  }

  /**
   * Check if in edit mode
   * @returns {boolean}
   */
  isEditMode() {
    return this.editMode;
  }

  /**
   * Get entry ID
   * @returns {string|null}
   */
  getEntryId() {
    return this.entryId;
  }

  /**
   * Mark field as touched
   * @param {string} fieldId - Field ID
   */
  touchField(fieldId) {
    this.touchedFields.add(fieldId);
    
    // Store initial value if not already stored
    if (!this.fieldInitialValues.has(fieldId)) {
      const field = document.querySelector(`[name="${fieldId}"], [id="${fieldId}"]`);
      if (field) {
        this.fieldInitialValues.set(fieldId, field.value || '');
      }
    }
  }

  /**
   * Check if field has been touched
   * @param {string} fieldId - Field ID
   * @returns {boolean}
   */
  isFieldTouched(fieldId) {
    return this.touchedFields.has(fieldId);
  }

  /**
   * Mark field as dirty
   * @param {string} fieldId - Field ID
   */
  markDirty(fieldId) {
    this.dirtyFields.add(fieldId);
  }

  /**
   * Check if field is dirty
   * @param {string} fieldId - Field ID
   * @returns {boolean}
   */
  isFieldDirty(fieldId) {
    return this.dirtyFields.has(fieldId);
  }

  /**
   * Update field value and check if dirty
   * @param {string} fieldId - Field ID
   * @param {string} newValue - New field value
   */
  updateFieldValue(fieldId, newValue) {
    const initialValue = this.fieldInitialValues.get(fieldId) || '';
    
    if (newValue !== initialValue) {
      this.markDirty(fieldId);
    }
  }

  /**
   * Set validation state for field
   * @param {string} fieldId - Field ID
   * @param {boolean} isValid - Validation state
   * @param {string} errorMessage - Error message if invalid
   */
  setValidationState(fieldId, isValid, errorMessage = '') {
    this.validationState.set(fieldId, {
      isValid,
      errorMessage
    });
  }

  /**
   * Get validation state for field
   * @param {string} fieldId - Field ID
   * @returns {Object|null}
   */
  getValidationState(fieldId) {
    return this.validationState.get(fieldId) || null;
  }

  /**
   * Check if field is valid
   * @param {string} fieldId - Field ID
   * @returns {boolean}
   */
  isFieldValid(fieldId) {
    const state = this.getValidationState(fieldId);
    return state ? state.isValid : true;
  }

  /**
   * Clear validation state for field
   * @param {string} fieldId - Field ID
   */
  clearValidationState(fieldId) {
    this.validationState.delete(fieldId);
  }

  /**
   * Clear all validation states
   */
  clearAllValidationStates() {
    this.validationState.clear();
  }

  /**
   * Set form data
   * @param {Object} data - Form data
   */
  setFormData(data) {
    this.formData = { ...data };
  }

  /**
   * Get form data
   * @returns {Object}
   */
  getFormData() {
    return { ...this.formData };
  }

  /**
   * Reset form state
   */
  reset() {
    this.editMode = false;
    this.entryId = null;
    this.dirtyFields.clear();
    this.touchedFields.clear();
    this.fieldInitialValues.clear();
    this.validationState.clear();
    this.formData = {};
  }

  /**
   * Get all invalid fields
   * @returns {Array}
   */
  getInvalidFields() {
    const invalid = [];
    this.validationState.forEach((state, fieldId) => {
      if (!state.isValid) {
        invalid.push({
          fieldId,
          errorMessage: state.errorMessage
        });
      }
    });
    return invalid;
  }

  /**
   * Check if form is valid
   * @returns {boolean}
   */
  isFormValid() {
    return this.validationState.size === 0 || 
           Array.from(this.validationState.values()).every(state => state.isValid);
  }
}

